// Mirrors the tier-selection + per-hour-scaling formula in server/data/rooms.js's computePrice() —
// keep the two in sync. Tier keys must match every room's pricing.public/member.options in
// src/data/rooms.js.
const TIER_KEYS = [0.25, 1, 2, 4, 8]

// Returns { tierHours, perHourRate, total } for a tiered room/duration/member-status, or null for
// a non-tiered room (no bulk-tier schedule) or missing pricing data. Used both for the live
// "billed at the Xhr rate ($Y/hr)" breakdown shown while picking a duration, and as the basis for
// the plain total returned by computeDisplayPrice() below.
export function getPricingBreakdown(room, durationHrs, isMember) {
  const p = isMember ? room.pricing.member : room.pricing.public
  if (!p || p.type !== 'tiered' || !durationHrs) return null

  const applicableTier = TIER_KEYS.filter(k => k <= Number(durationHrs)).pop() ?? TIER_KEYS[0]
  const opt = p.options.find(o => o.hours === applicableTier)
  if (!opt) return null

  const perHourRate = Math.round((opt.price / opt.hours) * 100) / 100
  const total = Math.round(perHourRate * Number(durationHrs) * 100) / 100
  return { tierHours: applicableTier, perHourRate, total }
}

// Plain total price for a room/duration/member-status — tiered rooms use getPricingBreakdown()
// above, non-tiered (flat hourly_rate) rooms just multiply rate * duration.
export function computeDisplayPrice(room, durationHrs, isMember) {
  if (!room?.pricing || !durationHrs) return null
  const p = isMember ? room.pricing.member : room.pricing.public
  if (!p) return null

  if (p.type === 'tiered') {
    return getPricingBreakdown(room, durationHrs, isMember)?.total ?? null
  }
  return p.rate ? Math.round(p.rate * Number(durationHrs) * 100) / 100 : null
}

// Formats an ISO instant as a Pacific-time clock ("2:30 PM") — used anywhere a booking's
// start/end needs a short human-readable time, e.g. the calendar's "Starting at ..." label and
// the booking-edit panels' duration sliders (src/pages/AdminDashboard.jsx, MemberDashboard.jsx).
export function formatTimePT(iso) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles', hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(new Date(iso))
}

// Always renders as hours/minutes (e.g. "1 hr 30 min", "45 min", "3 hrs") — never a decimal like
// "1.5 hrs". The underlying booking granularity is 15-minute increments, but that's an internal
// detail, not something customers should ever see as a raw fraction of an hour.
export function formatDuration(hrs) {
  const totalMinutes = Math.round(Number(hrs) * 60)
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60

  if (h === 0) return `${m} min`
  const hLabel = `${h} hr${h > 1 ? 's' : ''}`
  return m === 0 ? hLabel : `${hLabel} ${m} min`
}
